import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LEDGER_PARTY_ROLES,
  LEDGER_TYPES,
  schemas,
  type Ledger,
  type LedgerImportResult,
} from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { humanize } from '@/lib/format';
import { describeError } from '@/lib/errors';
import { FileField, Modal, ToneBadge } from '@/components/ui';
import { CrudPage } from './CrudPage';

/** What each kind of vendor account is for, in the words of the form. */
const PARTY_ROLE_LABEL: Record<(typeof LEDGER_PARTY_ROLES)[number], string> = {
  PAYABLE: 'Payable — what we owe them',
  ADVANCE: 'Advance — paid before their invoice',
  DEPOSIT: 'Deposit — security they hold',
  OTHER: 'Other',
};

/**
 * Each company's chart of accounts: the expense heads invoices are tagged
 * with, and each vendor's own accounts. Nothing is posted to them here — the
 * books stay in the ERP; fpc only records which ledger an invoice belongs to.
 */
export function LedgersPage() {
  const { companyId, can } = useAuth();
  const [importing, setImporting] = useState(false);

  const vendors = useQuery({
    queryKey: ['ledger-vendors', companyId],
    queryFn: () => api.settings.vendors({ companyId, pageSize: 200 }),
    enabled: !!companyId,
  });
  const vendorName = new Map((vendors.data?.items ?? []).map((v) => [v.id, v.name]));
  // Two kinds of ledger, chosen first so the form shows only what that kind needs.
  const isParty = (values: Record<string, unknown>) => values.kind === 'VENDOR';
  const isGeneral = (values: Record<string, unknown>) => !isParty(values);

  return (
    <>
      <CrudPage<Ledger>
        title="General Ledgers"
        subtitle="Each company's chart of accounts. Accounting tags every invoice with an expense ledger and the vendor's account; the books themselves stay in the ERP."
        queryKey="settings-ledgers"
        permissions={{
          read: 'ledger:read',
          create: 'ledger:create',
          update: 'ledger:update',
          delete: 'ledger:delete',
        }}
        list={(query) => api.settings.ledgers(query)}
        create={(body) => api.settings.createLedger(body)}
        update={(id, body) => api.settings.updateLedger(id, body)}
        remove={(id) => api.settings.deleteLedger(id)}
        restore={(id) => api.settings.updateLedger(id, { active: true })}
        schemas={{ create: schemas.createLedgerRequest, update: schemas.updateLedgerRequest }}
        headerActions={
          can('ledger:create') ? (
            <button type="button" className="btn-secondary" onClick={() => setImporting(true)}>
              Import
            </button>
          ) : null
        }
        columns={[
          {
            header: 'Ledger',
            render: (row) => (
              <span className="flex items-baseline gap-2">
                <span className="font-mono text-xs text-ink-500">{row.code}</span>
                <span className="font-medium">{row.name}</span>
              </span>
            ),
          },
          { header: 'Type', render: (row) => humanize(row.type) },
          { header: 'Group', render: (row) => row.group || '—' },
          {
            header: 'Vendor',
            render: (row) =>
              row.vendorId ? (
                <span className="flex flex-wrap items-center gap-1.5">
                  {vendorName.get(row.vendorId) ?? 'Vendor'}
                  <ToneBadge tone="neutral">{humanize(row.partyRole ?? 'OTHER')}</ToneBadge>
                  {row.isDefault ? <ToneBadge tone="positive">Default</ToneBadge> : null}
                </span>
              ) : (
                '—'
              ),
          },
        ]}
        fields={[
          {
            name: 'kind',
            label: 'What are you adding?',
            type: 'choice',
            transient: true,
            createOnly: true,
            options: [
              {
                value: 'GENERAL',
                label: 'General ledger',
                hint: 'What money is spent on or kept in — e.g. Rent, Housekeeping, Input GST, Bank. Not tied to any vendor.',
              },
              {
                value: 'VENDOR',
                label: 'Vendor account',
                hint: 'What the company owes or has paid one vendor — e.g. Proserve – Payable, Proserve – Advance.',
              },
            ],
          },
          {
            name: 'vendorId',
            label: 'Vendor',
            type: 'select',
            required: true,
            visibleWhen: isParty,
            options: (vendors.data?.items ?? []).map((v) => ({ value: v.id, label: v.name })),
          },
          {
            name: 'partyRole',
            label: 'Account for',
            type: 'select',
            required: true,
            visibleWhen: isParty,
            options: LEDGER_PARTY_ROLES.map((role) => ({
              value: role,
              label: PARTY_ROLE_LABEL[role],
            })),
          },
          {
            name: 'code',
            label: 'Code',
            help: 'e.g. 5020 for a general ledger, PROSERVE-PAY for a vendor account. Generated from the name if left blank.',
          },
          { name: 'name', label: 'Name', required: true },
          {
            name: 'type',
            label: 'Type',
            type: 'select',
            required: true,
            visibleWhen: isGeneral,
            options: LEDGER_TYPES.map((type) => ({ value: type, label: humanize(type) })),
            help: 'Expense and Asset ledgers are the ones offered on invoices.',
          },
          {
            name: 'group',
            label: 'Group',
            visibleWhen: isGeneral,
            help: 'The parent it rolls up into, e.g. Indirect Expenses.',
          },
          {
            name: 'isDefault',
            label: 'Use as this vendor’s default account',
            type: 'checkbox',
            visibleWhen: isParty,
          },
        ]}
        toFormValues={(row) => ({ ...row, kind: row.vendorId ? 'VENDOR' : 'GENERAL' })}
        defaults={{ kind: 'GENERAL', type: 'EXPENSE' }}
        formColumns={2}
      />
      {importing && companyId ? (
        <ImportLedgersModal companyId={companyId} onClose={() => setImporting(false)} />
      ) : null}
    </>
  );
}

/** Checks a chart of accounts file first, then imports it in one go. */
function ImportLedgersModal({ companyId, onClose }: { companyId: string; onClose(): void }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [checked, setChecked] = useState<LedgerImportResult | null>(null);

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api.settings.importLedgers({ companyId, file: file!, dryRun }),
    onSuccess: (result) => {
      setChecked(result);
      if (!result.dryRun) void queryClient.invalidateQueries({ queryKey: ['settings-ledgers'] });
    },
  });
  const done = checked && !checked.dryRun;
  const ready = checked?.dryRun && !checked.errors.length;

  return (
    <Modal
      title="Import ledgers"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {done ? 'Close' : 'Cancel'}
          </button>
          {done ? null : ready ? (
            <button
              type="button"
              className="btn-primary"
              disabled={run.isPending}
              onClick={() => run.mutate(false)}
            >
              Import {checked.created + checked.updated} ledgers
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary"
              disabled={!file || run.isPending}
              onClick={() => run.mutate(true)}
            >
              Check file
            </button>
          )}
        </>
      }
    >
      <p className="mb-3 text-sm text-ink-600">
        A spreadsheet with <b>Code</b>, <b>Name</b> and <b>Type</b> columns (Asset, Liability,
        Income, Expense or Equity), and optionally <b>Group</b>. For a vendor’s own account, add{' '}
        <b>Vendor Code</b> and <b>Party Role</b> (Payable, Advance, Deposit). A ledger whose code
        already exists is updated.
      </p>
      <FileField
        id="ledger-import-file"
        file={file}
        accept=".xlsx,.csv"
        onChange={(next) => {
          setFile(next);
          setChecked(null);
        }}
        disabled={run.isPending || !!done}
      />

      {run.error ? <p className="mt-3 text-sm text-red-700">{describeError(run.error)}</p> : null}
      {checked ? (
        <div className="mt-4 space-y-2 text-sm">
          <p className={checked.errors.length ? 'notice-warning' : 'notice-info'}>
            {done
              ? `Imported: ${checked.created} added, ${checked.updated} updated.`
              : checked.errors.length
                ? `${checked.errors.length} row(s) need fixing before anything is imported.`
                : `Ready: ${checked.created} to add, ${checked.updated} to update.`}
          </p>
          {checked.errors.length ? (
            <ul className="max-h-48 list-disc space-y-1 overflow-y-auto pl-5 text-red-700">
              {checked.errors.map((error) => (
                <li key={`${error.row}-${error.message}`}>
                  Row {error.row}: {error.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
